import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  PosMarketRecord,
  PosMarketDeleteRequest,
  PosMarketSearchParameter,
  PosSearchRequest,
  TYPE_SUCCESS,
  UppComponent,
  UppComponentNames,
  PosMarketDetail
} from '../../service/model';
import {
  SearchPosAction,
  ClearPosResultsAction,
  DeletePosAction,
  ActivatePosDisplayAction,
  StartPosModificationAction,
  DeletePosSearchNotificationAction,
  DeletePosDisplayNotificationAction,
  CopyPosAction,
  DeletePosCreateNotificationAction,
  ClearPosSelectionAction
} from '../../store/pos/pos-actions';
import {
  selectPosDeleteSending,
  selectPosRecords,
  selectPosSearchSending,
  selectPosSearchNotification,
  selectSelectedPosNames
} from '../../store/pos/pos-selector';
import { NavigationService } from '../../service/core/navigation.service';
import { Router } from '@angular/router';
import { PosSearchCriteriaComponent } from './pos-search-criteria/pos-search-criteria.component';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import { UppNotification } from '../../model/notification';
import { selectNavigation } from '../../store/navigation/navigation-selector';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType,
  SearchResult
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import { PERMISSION_MANAGE_POS } from '../../service/user-permissions.service';
import { PosMarketService } from '../../service/pos-market.service';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { map, take, takeUntil, shareReplay, tap } from 'rxjs/operators';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../../core/util/resources';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';

@Component({
  selector: 'ama-ng-upp-pos-search',
  templateUrl: './pos-search.component.html',
  styleUrls: ['./pos-search.component.scss'],
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    PosSearchCriteriaComponent,
    AgGridAngular,
    AsyncPipe,
    BasicSearchResultTableComponent
  ]
})
export class PosSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(PosSearchCriteriaComponent)
  posSearchCriteriaComponent!: PosSearchCriteriaComponent;

  retrievedPosMarkets$: Observable<PosMarketRecord[] | undefined>;
  retrievedPosMarketsLookup$: Observable<PosMarketRecord[]> = of([]);
  selectedPosNames$: Observable<Set<string> | undefined>;
  posSearchSending$: Observable<boolean>;
  posDeleteSending$: Observable<boolean>;
  notificationSearch$: Observable<UppNotification | undefined>;
  notificationLookup?: UppNotification;

  uppComponent = UppComponent.POS_SEARCH;
  mainMessages!: NotificationMessages;

  managePermission = PERMISSION_MANAGE_POS;

  agGridTablesActivated = false;

  displayableFields: DisplayableField[] = [
    {
      value: 'description',
      label: 'Description'
    }
  ];

  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);

  posMarketSearchResults?: PosMarketRecord[];
  selectedPosNames?: Set<string>;
  private readonly subscription = new Subscription();

  private readonly configurationService: ConfigurationService;
  private readonly posMarketsService: PosMarketService;

  constructor(
    store: Store,
    router: Router,
    navigationService: NavigationService,
    configurationService: ConfigurationService,
    posMarketsService: PosMarketService
  ) {
    super(store, router, navigationService);

    this.configurationService = configurationService;
    this.posMarketsService = posMarketsService;
    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((agGridTables) => {
        this.agGridTablesActivated = agGridTables ?? false;
      })
    );
    this.retrievedPosMarkets$ = this.store.pipe(select(selectPosRecords));
    this.selectedPosNames$ = this.store.pipe(select(selectSelectedPosNames));
    this.posSearchSending$ = this.store.pipe(
      select(selectPosSearchSending),
      map((sending) => !!sending)
    );
    this.posDeleteSending$ = this.store.pipe(
      select(selectPosDeleteSending),
      map((sending) => !!sending)
    );
    this.notificationSearch$ = this.store.pipe(select(selectPosSearchNotification));

    this.initializeColumnDefs();
  }

  ngOnInit() {
    super.ngOnInit();

    this.navigationService.enableNavigation();
    this.store.dispatch(new ClearPosSelectionAction({}));
    this.navigationService.setSelectedMenuTitle('POS Search');
    this.navigationService.activatePos();

    if (!this.agGridTablesActivated) {
      this.store.pipe(select(selectNavigation), take(1)).subscribe((lookupParam) => {
        if (lookupParam) {
          this.lookupOptions = lookupParam;
          this.lookupValues = lookupParam.data || [];
          this.lookup = !!lookupParam.lookup;
        }
      });
    }

    if (this.lookup) {
      this.navigationService.disableNavigation();
      if (this.agGridTablesActivated) {
        this.updateColumnDefsForLookup();
      }
    }

    const navigationSubscription = this.store
      .pipe(select(selectNavigation), takeUntil(this.destroy$))
      .subscribe((lookupParam) => {
        if (lookupParam && lookupParam.lookup) {
          this.navigationService.disableNavigation();
        }
      });
    this.subscription.add(navigationSubscription);

    const posMarketsSubscription = this.retrievedPosMarkets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((posMarketSearchResultsArray) => (this.posMarketSearchResults = posMarketSearchResultsArray));
    this.subscription.add(posMarketsSubscription);

    const posNamesSubscription = this.selectedPosNames$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedPosNamesSearchResults) => (this.selectedPosNames = selectedPosNamesSearchResults));
    this.subscription.add(posNamesSubscription);

    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };
  }

  ngOnDestroy() {
    this.subscription.unsubscribe(); // Clean up component-specific subscriptions
    super.ngOnDestroy(); // Call base class ngOnDestroy
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.POS;
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => {
      this.showPosDetail((data as PosMarketDetail).id ?? '');
    };
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyPosDetail((data as PosMarketDetail).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return (data: unknown) => {
      this.store.dispatch(new CopyPosAction({ id: (data as PosMarketDetail).id ?? '' }));
      this.router.navigate(['pos/create/']);
    };
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const request: PosMarketDeleteRequest = {
        version: '1.0',
        pointOfSale: {
          id: (data as PosMarketDetail).id || '',
          name: (data as PosMarketDetail).name || '',
          organization: (data as PosMarketDetail).organization || '',
          version: (data as PosMarketDetail).version || 1
        }
      };
      this.store.dispatch(new DeletePosAction({ request }));
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return () => {};
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.posDeleteSending$;
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId); // Notify all child components
  }

  searchPosList(searchParameter: PosMarketSearchParameter) {
    const request = this.createSearchPosRequest(searchParameter);
    if (this.lookup) {
      if (!this.agGridTablesActivated) {
        this.retrievedPosMarketsLookup$ = this.posMarketsService.lookupPos(request);
        this.retrievedPosMarketsLookup$.subscribe((result) => (this.notificationLookup = result[0].statusNotification));
      } else {
        this.retrievedPosMarketsLookup$ = this.posMarketsService.lookupPos(request).pipe(
          take(1),
          shareReplay(1),
          tap((results: PosMarketRecord[]) => {
            if (results && results.length > 0) {
              this.notificationLookup = results[0].statusNotification;
            }
          }),
          takeUntil(this.destroy$)
        );
      }
    } else {
      this.store.dispatch(new SearchPosAction({ request }));
    }

    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  clearPos() {
    this.store.dispatch(new ClearPosResultsAction({}));
    this.store.dispatch(new ClearPosSelectionAction({}));
    if (this.lookup) {
      this.retrievedPosMarketsLookup$ = of([]);
      this.lookupValues = [];
      this.notificationLookup = undefined;
    }
  }

  showPosDetail(id: string) {
    this.store.dispatch(new ActivatePosDisplayAction({ id: undefined }));
    this.store.dispatch(new DeletePosDisplayNotificationAction({}));
    this.router.navigate(['pos/display/' + id]);
  }

  modifyPosDetail(detailId: string) {
    this.store.dispatch(new StartPosModificationAction({ id: detailId }));
    this.router.navigate(['pos/modify/']);
  }

  copyPosDetail(detailId: string) {
    this.store.dispatch(new CopyPosAction({ id: detailId }));
    this.store.dispatch(new DeletePosCreateNotificationAction({}));
    this.router.navigate(['pos/create/']);
  }

  deletePosEntry(request: PosMarketDeleteRequest) {
    this.store.dispatch(new DeletePosAction({ request }));
  }

  closeNotification() {
    if (this.lookup) {
      this.notificationLookup = undefined;
    } else {
      this.store.dispatch(new DeletePosSearchNotificationAction({}));
    }
  }

  createSearchPosRequest(searchParameter: PosMarketSearchParameter): PosSearchRequest {
    return { version: '1.0', pointOfSale: searchParameter };
  }

  isErrorMessageAvailable(): boolean {
    if (this.posMarketSearchResults) {
      return this.posMarketSearchResults.filter((record) => record.statusMessage !== undefined).length > 0;
    }
    return false;
  }

  filterErrorMessages(): string[] {
    const errorMessages: string[] = [];
    if (this.posMarketSearchResults) {
      this.posMarketSearchResults.forEach((record) => {
        if (record.statusType !== TYPE_SUCCESS) {
          errorMessages.push(record.statusMessage?.[0] || '');
        }
      });
    }
    return errorMessages;
  }

  storeUnsavedPosSearch() {
    if (!this.lookup) {
      this.posSearchCriteriaComponent.storeUnsavedPosSearch();
    }
  }

  getEntryType(): EntryType {
    return EntryType.POINT_OF_SALE;
  }

  getSearchResults(): Observable<PosMarketDetail[] | PosMarketRecord[] | undefined> {
    const sourceObservable = this.lookup ? this.retrievedPosMarketsLookup$ : this.retrievedPosMarkets$;

    if (!this.agGridTablesActivated) {
      return sourceObservable;
    }

    return sourceObservable.pipe(
      take(1),
      map((data) => this.extractPosMarketDetails(data))
    );
  }

  getNotification(): Observable<UppNotification | undefined> {
    if (this.lookup) {
      return of(this.notificationLookup);
    }
    return this.notificationSearch$.pipe(take(1));
  }

  recordToSearchResult(posRecord: PosMarketRecord): SearchResult {
    const posMarketDetail = posRecord.posMarketDetail;
    return {
      id: posMarketDetail?.id,
      name: posMarketDetail?.name,
      description: posMarketDetail?.description,
      organization: posMarketDetail?.organization,
      version: posMarketDetail?.version
    };
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): PosMarketDeleteRequest {
    return {
      version,
      pointOfSale: {
        id,
        name,
        organization,
        version: ruleVersion
      }
    };
  }

  entryToUpdateNameResolver(entryToUpdate: PosMarketDeleteRequest) {
    return entryToUpdate.pointOfSale?.name;
  }

  /**
   * Updates the column definitions for lookup mode
   */
  protected updateColumnDefsForLookup(): void {
    this.columnDefs = [
      this.columnDefs[0], // Name column
      this.columnDefs[1] // Description column
    ];
  }

  private initializeColumnDefs() {
    this.columnDefs = [
      {
        headerName: 'Name',
        field: 'name',
        resizable: false,
        unSortIcon: true,
        suppressMovable: true,
        autoHeight: true
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        wrapText: true,
        minWidth: 250,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          posDeleteSending$: this.posDeleteSending$
        }
      }
    ];
  }

  private extractPosMarketDetails(data: PosMarketRecord[] | null | undefined): PosMarketDetail[] {
    if (!data) {
      return [];
    }

    return data
      .filter((record) => record?.posMarketDetail)
      .map((record) => record.posMarketDetail)
      .filter((detail) => !!detail);
  }
}
